/**
 * Authentication Context
 *
 * Manages global authentication state using HTTP-only cookies for JWT.
 * The backend sets the JWT token as an HTTP-only cookie, so we don't
 * store it in localStorage (more secure against XSS attacks).
 * Provides authentication methods to child components.
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useWalletInfo } from '@reown/appkit/react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { usePhantom } from './PhantomContext';

import { useWalletAddress } from '@/hooks/useWalletAddress';
import * as authApi from '@/api/auth';
import { logger } from '@/lib/logger';
import { setSentryUser } from '@/lib/sentry';

interface UserInfo {
  address: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  userInfo: UserInfo | null;
  signInWithWallet: (signature: string, message: string, walletProviderName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserInfo: () => Promise<void>;
  sessionExpired: boolean;
  setSessionExpired: (expired: boolean) => void;
  isVerifyingSession: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const USER_INFO_STORAGE_KEY = 'junk_fun_user_info';
const REMEMBER_ME_STORAGE_KEY = 'junk_fun_remember_me';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { address, isConnected, walletType } = useWalletAddress();
  const { walletInfo } = useWalletInfo('solana');
  const { disconnectPhantom, isPhantomConnected } = usePhantom();

  const activeProvider = walletType;

  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Track whether we're currently verifying an existing session with the backend
  // Starts as false - will be set to true when verification begins
  const [isVerifyingSession, setIsVerifyingSession] = useState(false);
  const queryClient = useQueryClient();
  const hasShownToast = useRef(false);
  // Track initial load phase to prevent disconnect handler from running on mount
  const isInitialLoadRef = useRef(true);

  /**
   * Sign out and clear auth state
   * Clears HTTP-only cookie on backend
   */
  const signOut = useCallback(async () => {
    try {
      await authApi.signOut();
    } catch (error) {
      logger.error('Sign out error:', error);
      // Continue with local cleanup even if backend call fails
    }

    // Disconnect Phantom if it's the active provider
    if (activeProvider === 'phantom' && isPhantomConnected) {
      try {
        await disconnectPhantom();
      } catch (error) {
        logger.error('Error disconnecting Phantom:', error);
      }
    }

    setUserInfo(null);
    setSessionExpired(false);
    setIsVerifyingSession(false);
    hasShownToast.current = false;
    localStorage.removeItem(USER_INFO_STORAGE_KEY);
    localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
    // Clear all React Query cache to prevent stale data when switching accounts
    queryClient.clear();
    // Clear Sentry user context
    setSentryUser(null);
  }, [queryClient, activeProvider, isPhantomConnected, disconnectPhantom]);

  /**
   * Refresh user info from backend
   * Useful to verify session is still valid
   */
  const refreshUserInfo = useCallback(async () => {
    try {
      const response = await authApi.getUserInfo();
      if (response.code !== 0) {
        throw new Error(response.message || 'Failed to get user info');
      }
      logger.log('[AuthContext] 🔄 Refreshed user info from backend:', response.data);
      logger.log('[AuthContext] 🔄 Wallet provider from backend:', response.data.wallet_provider);
      setUserInfo(response.data);
      localStorage.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(response.data));
    } catch (error) {
      // Session expired or invalid
      setUserInfo(null);
      localStorage.removeItem(USER_INFO_STORAGE_KEY);
      throw error;
    }
  }, []);

  /**
   * Sign in with wallet signature
   * Backend sets JWT token as HTTP-only cookie
   * @param signature - Wallet signature
   * @param message - Message that was signed
   * @param walletProviderName - Optional: The wallet provider name detected by the caller
   */
  const signInWithWallet = async (signature: string, message: string, walletProviderName?: string) => {
    if (!address) {
      throw new Error('Wallet not connected');
    }

    // Use the wallet provider name passed in (detected from the actual signing wallet)
    // Fall back to detecting if not provided
    let detectedWalletName = walletProviderName;

    if (!detectedWalletName) {
      logger.log('[AuthContext] ⚠️ No wallet provider passed, attempting fallback detection');

      // Check if Phantom is the active provider
      if (activeProvider === 'phantom') {
        detectedWalletName = 'Phantom';
        logger.log('[AuthContext] 🎯 Detected Phantom from active provider');
      }
      // Fallback: Try to detect from walletInfo (with normalization for known wallets)
      else if (walletInfo?.name) {
        const walletName = walletInfo.name;
        const walletNameLower = walletName.toLowerCase();
        const rdnsLower = ((walletInfo.rdns as string) || '').toLowerCase();

        // Gate Wallet detection
        if (walletNameLower.includes('gate') || rdnsLower.includes('gate')) {
          detectedWalletName = 'Gate Wallet';
        }
        // Solflare detection
        else if (walletNameLower.includes('solflare') || rdnsLower.includes('solflare')) {
          detectedWalletName = 'Solflare';
        }
        // Backpack detection
        else if (walletNameLower.includes('backpack') || rdnsLower.includes('backpack')) {
          detectedWalletName = 'Backpack';
        }
        // Phantom detection
        else if (walletNameLower.includes('phantom') || rdnsLower.includes('phantom')) {
          detectedWalletName = 'Phantom';
        }
        // Use the name as-is for other wallets
        else {
          detectedWalletName = walletName;
        }
        logger.log('[AuthContext] 🎯 Fallback from walletInfo.name:', detectedWalletName);
      }
      // Last resort: check window.solana (unreliable with multiple wallets)
      else if (typeof window !== 'undefined') {
        const solanaWallet = (window as any).solana;
        if (solanaWallet) {
          // Check for Gate Wallet first (before Phantom)
          if ((window as any).gatewallet || solanaWallet.isGateWallet) {
            detectedWalletName = 'Gate Wallet';
          } else if (solanaWallet.isBackpack) {
            detectedWalletName = 'Backpack';
          } else if ((window as any).solflare?.isSolflare) {
            detectedWalletName = 'Solflare';
          } else if (solanaWallet.isPhantom) {
            detectedWalletName = 'Phantom';
          }
        }
      }
    }

    logger.log('[AuthContext] ✅ Using wallet provider:', detectedWalletName || 'undefined');

    const response = await authApi.signIn({
      address,
      message,
      signature,
      account_type: 'SVM', // Solana
      wallet_provider: detectedWalletName,
    });

    if (response.code !== 0) {
      throw new Error(response.message || 'Sign in failed');
    }

    // JWT token is set as HTTP-only cookie by backend
    // We only store user info locally for quick access
    setUserInfo(response.data);
    setSessionExpired(false);
    hasShownToast.current = false;
    localStorage.setItem(USER_INFO_STORAGE_KEY, JSON.stringify(response.data));
    localStorage.setItem(REMEMBER_ME_STORAGE_KEY, 'true');

    // Set Sentry user context for better error tracking
    setSentryUser({ walletAddress: address });

    // Invalidate queries that depend on authentication state
    queryClient.invalidateQueries({ queryKey: ['userPoints'] });
    queryClient.invalidateQueries({ queryKey: ['pointsLottery'] });
  };

  // Load user info from localStorage on mount
  useEffect(() => {
    const storedUserInfo = localStorage.getItem(USER_INFO_STORAGE_KEY);
    const rememberMe = localStorage.getItem(REMEMBER_ME_STORAGE_KEY) === 'true';

    // Debug logging in development
    if (process.env.NODE_ENV === 'development') {
      logger.log('[AuthContext] 🔍 Mount - Checking persisted state', {
        hasStoredUser: !!storedUserInfo,
        rememberMe,
        isConnected,
        walletAddress: address,
      });
    }

    if (storedUserInfo && rememberMe) {
      try {
        const parsedUserInfo = JSON.parse(storedUserInfo);
        logger.log('[AuthContext] 💾 Loaded user info from localStorage:', parsedUserInfo);
        logger.log('[AuthContext] 💾 Wallet provider from localStorage:', parsedUserInfo.wallet_provider);

        // Optimistically set user info from localStorage for faster UI
        setUserInfo(parsedUserInfo);
        // Set Sentry user context from restored session
        setSentryUser({ walletAddress: parsedUserInfo.address });
      } catch (error) {
        logger.error('Failed to parse stored user info:', error);
        localStorage.removeItem(USER_INFO_STORAGE_KEY);
        localStorage.removeItem(REMEMBER_ME_STORAGE_KEY);
      }
    } else {
      // No valid stored session - mark verification as complete immediately
      setIsVerifyingSession(false);
      if (storedUserInfo && !rememberMe) {
        logger.log('[AuthContext] 🧹 Clearing user info (no remember me flag)');
        localStorage.removeItem(USER_INFO_STORAGE_KEY);
      }
    }

    // Mark initial load phase as complete after wallet has time to reconnect
    const loadTimeout = setTimeout(() => {
      isInitialLoadRef.current = false;
    }, 3000);

    return () => clearTimeout(loadTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Track if we've verified the session for this load
  const sessionVerifiedRef = useRef(false);

  // Verify session after initial load
  useEffect(() => {
    // Skip if no stored user info
    if (!userInfo) {
      return;
    }

    // Skip if we've already verified this session
    if (sessionVerifiedRef.current) {
      return;
    }

    // Perform verification after a short delay to allow wallet to reconnect
    const verifyTimeout = setTimeout(async () => {
      // Mark as verified so we don't retry
      sessionVerifiedRef.current = true;

      // Check if wallet address matches (if wallet is connected)
      if (isConnected && address && address.toLowerCase() !== userInfo.address.toLowerCase()) {
        logger.log('[AuthContext] 🔄 Wallet address mismatch - clearing session');
        signOut();
        return;
      }

      // Now verify the session with backend
      logger.log('[AuthContext] 🔍 Verifying session for:', userInfo.address);
      setIsVerifyingSession(true);

      try {
        await refreshUserInfo();
        logger.log('[AuthContext] ✅ Session verified successfully');
        setIsVerifyingSession(false);
      } catch (error: any) {
        setIsVerifyingSession(false);

        // Check if it's a real auth error (401/403) or just network issue
        const isAuthError = error?.status === 401 || error?.status === 403;

        if (process.env.NODE_ENV === 'development') {
          logger.log('[AuthContext] ❌ Session verification failed', {
            error,
            errorStatus: error?.status,
            errorMessage: error?.message,
            isAuthError,
            currentWallet: address,
            storedWallet: userInfo.address,
            isConnected
          });
        }

        // Only clear session if it's an actual auth error
        if (isAuthError) {
          logger.log('[AuthContext] 🔄 Session expired - will trigger re-authentication');
          setSessionExpired(true);
          // Keep userInfo so auto-auth knows which wallet to authenticate

          // Only show toast if wallet is not connected
          // (if connected, auto-auth will handle it silently)
          if (!isConnected && !hasShownToast.current) {
            toast.info('Please connect your wallet to continue', {
              duration: 5000,
            });
            hasShownToast.current = true;
          }
        } else {
          // Network error - keep the session
          logger.log('[AuthContext] ⚠️ Network error during verification - keeping session');
        }
      }
    }, 1500); // Wait 1.5 seconds for wallet to potentially reconnect

    return () => clearTimeout(verifyTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.address]); // Only depend on userInfo.address to avoid re-runs

  // Clear auth state when wallet disconnects
  useEffect(() => {
    // Skip during initial load phase to prevent clearing session on mount
    // (wallet reports isConnected=false before it has a chance to reconnect)
    if (isInitialLoadRef.current) {
      logger.log('[AuthContext] ⏳ Skipping disconnect handler during initial load');
      return;
    }

    if (!isConnected && (userInfo || sessionExpired)) {
      setSessionExpired(false);
      hasShownToast.current = false;
      if (userInfo) {
        signOut();
      }
    }
  }, [isConnected, userInfo, sessionExpired, signOut]);

  // Clear auth state when wallet address changes (user switched accounts)
  useEffect(() => {
    if (isConnected && userInfo && address && address !== userInfo.address) {
      console.log('Wallet address changed, clearing auth state');
      signOut();
    }
  }, [address, isConnected, userInfo, signOut]);

  const value: AuthContextType = {
    isAuthenticated: !!userInfo,
    userInfo,
    signInWithWallet,
    signOut,
    refreshUserInfo,
    sessionExpired,
    setSessionExpired,
    isVerifyingSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use authentication context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useAuthContext() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
